import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootState, useAppDispatch, useAppSelector } from '../../../../../state/state';
import { closeModal, toggleLoading } from '../../../../../state/ModalSlice/ModalSlice';
import { toast } from 'react-toastify';
import { addDoc, collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';

type Props = {
  boardId: string | null;
};

type Column = { id: string; title: string; index: number };

export default function AddItemModal({ boardId }: Props) {
  const open = useAppSelector((state: RootState) => state.ModalSlice.AddItemModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const user = useAppSelector((state: RootState) => state.userSliceV2.data);
  const userId = user?.uid || '';
  const dispatch = useAppDispatch();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [columnId, setColumnId] = useState<string>('');
  const [columns, setColumns] = useState<Column[]>([]);

  useEffect(() => {
    if (!userId || !boardId) return;
    const q = query(collection(db, `ServiceProviders/${userId}/InflowBoards/${boardId}/Columns`), orderBy('index', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const cols: Column[] = [];
      snap.forEach((d) => cols.push({ id: d.id, ...(d.data() as Omit<Column, 'id'>) }));
      setColumns(cols);
      if (cols.length && !columnId) setColumnId(cols[0].id);
    });
    return () => unsub();
  }, [userId, boardId]);

  const handleClose = () => {
    dispatch(closeModal('AddItemModalOpen'));
    setTitle('');
    setDescription('');
    setColumnId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !boardId) {
      toast.error('Missing user or board');
      return;
    }
    if (!title.trim() || !columnId) {
      toast.error('Title and column are required');
      return;
    }
    try {
      dispatch(toggleLoading(true));
      const itemsRef = collection(db, `ServiceProviders/${userId}/InflowBoards/${boardId}/Items`);
      await addDoc(itemsRef, {
        title: title.trim(),
        description: description.trim() || null,
        columnId,
        order: Date.now(),
        createdAt: Date.now(),
        createdBy: user?.display_name || user?.email || 'Unknown',
      });
      toast.success('Item added');
      handleClose();
    } catch (e) {
      console.error(e);
      toast.error('Failed to add item');
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <Typography variant="h6">Add Card</Typography>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={isLoading} />
          <TextField fullWidth label="Description" value={description} onChange={(e) => setDescription(e.target.value)} disabled={isLoading} multiline rows={3} sx={{ mt: 2 }} />
          <FormControl fullWidth sx={{ mt: 2 }} disabled={isLoading || !columns.length}>
            <InputLabel id="column-select-label">Column</InputLabel>
            <Select labelId="column-select-label" label="Column" value={columnId} onChange={(e) => setColumnId(e.target.value as string)}>
              {columns.map((c) => (
                <MenuItem value={c.id} key={c.id}>{c.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button type="submit" variant="contained" sx={{ mt: 2 }} fullWidth disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}>Add</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}


